/* eslint-disable no-nested-ternary */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { baseUrl } from '../utils/constants';

export const DEFAULT_CHANGE_TYPE = 1;
export const DEFAULT_PROVIDER = 1;
export const DEFAULT_USER_REF = 1;

export const FALLBACK_PROVINCES = [
  'BULAWAYO',
  'Harare',
  'Manicaland',
  'Mashonaland Central',
  'Mashonaland East',
  'Mashonaland West',
  'Masvingo',
  'Matabeleland North',
  'Matabeleland South',
  'Midlands'
];

const OnOffContext = createContext(null);

const pick = (row, keys) => {
  const entry = Object.entries(row).find(([key, value]) => {
    const normalised = key.toLowerCase().replace(/[^a-z0-9]/g, '');
    return keys.includes(normalised) && value != null && value !== '';
  });

  return entry ? String(entry[1]).trim() : null;
};

const mapRecord = (row, index) => {
  const empNo = pick(row, ['empno', 'employeenumber', 'employeeno', 'membernumber']) ?? '';
  const clientRefId = pick(row, ['clientrefid']) ?? 0;
  const refId = pick(row, ['refid']) ?? 0;
  return {
    id: refId || empNo || `row-${index}`,
    clientRefId,
    emp_no: empNo,
    membername: pick(row, ['membername']),
    initials: pick(row, ['initials', 'initial', 'firstname']) ?? '',
    subsection: pick(row, ['subsection']),
    department: pick(row, ['department', 'dept']),
    stat: pick(row, ['stat', 'station']),
    station_description: pick(row, ['stationdescription', 'stationdesc']),
    gender: pick(row, ['gender', 'sex']),
    status: pick(row, ['status']) ?? '',
    on_off: (pick(row, ['onoff', 'onoffstatus', 'payrollstatus']) ?? 'Off')
      .toLowerCase()
      .startsWith('on')
      ? 'On'
      : 'Off',
    area_code: pick(row, ['areacode']),
    area_desc: pick(row, ['desc', 'areadesc', 'areadescription', 'area']),
    nat_reg_no: pick(row, ['natregno', 'nationalregistrationnumber', 'idnumber', 'nationalid']),
    phone: pick(row, [
      'phone',
      'phonenumber',
      'cellnumber',
      'cellphone',
      'mobile',
      'mobilenumber',
      'contactnumber',
      'msisdn'
    ]),
    province: pick(row, ['province', 'provincename', 'provincedesc', 'provincecode']) ?? ''
  };
};

const collectProvinces = (rows) => {
  const byLower = new Map();
  const canonical = new Map(FALLBACK_PROVINCES.map((p) => [p.toLowerCase(), p]));
  rows.forEach((row) => {
    Object.entries(row).forEach(([key, value]) => {
      if (typeof value !== 'string' || !value.trim()) return;
      const normalised = key.toLowerCase().replace(/[^a-z0-9]/g, '');
      const lower = value.trim().toLowerCase();
      if (!normalised.includes('province') && !canonical.has(lower)) return;
      if (!byLower.has(lower)) byLower.set(lower, canonical.get(lower) ?? value.trim());
    });
  });
  return Array.from(byLower.values()).sort((a, b) => a.localeCompare(b));
};

const readStore = (key) => {
  try {
    return JSON.parse(window.localStorage.getItem(key) ?? '{}');
  } catch {
    return {};
  }
};

export function OnOffProvider({ children }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState({});
  const [sms, setSms] = useState({});
  const [phones, setPhones] = useState({});
  const [provinces, setProvinces] = useState([]);

  useEffect(() => {
    setNotes(readStore('mpr-notes'));
    setSms(readStore('mpr-sms'));
    setPhones(readStore('mpr-phones'));
  }, []);

  const persist = (key, value) => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* storage full or unavailable */
    }
  };

  const fetchMembers = useCallback(
    async (from, to, changeType = DEFAULT_CHANGE_TYPE, provider = DEFAULT_PROVIDER) => {
      setLoading(true);
      setError(null);
      try {
        console.log('from ', from);
        const response = await fetch(
          `${baseUrl}/api/client/GetMembershipChanges?from=${from}&to=${to}&documentType=${changeType}&userID=${provider}`
        );
        if (!response.ok)
          throw new Error(`The member service responded with status ${response.status}.`);
        const payload = await response.json();
        const rows = Array.isArray(payload)
          ? payload
          : payload?.data ?? payload?.records ?? payload?.result ?? [];
        const mapped = (Array.isArray(rows) ? rows : []).map(mapRecord);
        console.log('mapped data ', mapped);
        setMembers(mapped);
        return mapped.length;
      } catch (err) {
        const message =
          err instanceof Error && err.message.startsWith('The member')
            ? err.message
            : 'Could not reach the member service.';
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /** Province list for the filter dropdown, filled from the reports service. */
  const fetchProvinces = useCallback(async () => {
    try {
      const response = await fetch(
        `${baseUrl}/api/client/GetMembershipChanges?from=80&to=80&documentType=${DEFAULT_CHANGE_TYPE}&userID=${DEFAULT_PROVIDER}`
      );
      if (!response.ok)
        throw new Error(`The member service responded with status ${response.status}.`);
      const payload = await response.json();

      const rows = Array.isArray(payload)
        ? payload
        : payload?.data ?? payload?.records ?? payload?.result ?? [];
      const list = collectProvinces(Array.isArray(rows) ? rows : []);
      setProvinces(list.length ? list : FALLBACK_PROVINCES);
    } catch {
      setProvinces(FALLBACK_PROVINCES);
    }
  }, []);

  const addNote = useCallback(async (member, { title, content }) => {
    const url =
      `${baseUrl}/api/client/AddClientNote?clientRefId=${encodeURIComponent(member.clientRefId)}` +
      `&title=${encodeURIComponent(title)}&content=${encodeURIComponent(
        content
      )}&userRefId=${DEFAULT_USER_REF}`;
    const response = await fetch(url, { method: 'POST' });
    if (!response.ok) throw new Error(`Note service responded with status ${response.status}.`);
    setNotes((prev) => {
      const entry = { id: `${Date.now()}`, title, content, created_at: new Date().toISOString() };
      const next = { ...prev, [member.id]: [entry, ...(prev[member.id] ?? [])] };
      persist('mpr-notes', next);
      return next;
    });
  }, []);

  /** Previous notes for a member, loaded from the client service (not localStorage). */
  const fetchNotes = useCallback(async (member) => {
    const cif = member.clientRefId || member.emp_no;
    const url = `${baseUrl}/api/client/GetNotesForClient?cif_number=${encodeURIComponent(
      cif
    )}&step=0&skip=10&orderBy=Date&order=false`;
    const response = await fetch(url);
    if (!response.ok)
      throw new Error(`The notes service responded with status ${response.status}.`);
    const payload = await response.json();

    // The service returns { notesList: [...], totalRecords, replyStatus, replyMessage }
    const rows = payload?.notesList ?? payload?.NotesList ?? payload;
    const list = (Array.isArray(rows) ? rows : []).map((row, index) => ({
      id: String(row.id ?? row.noteId ?? index),
      title: row.title ?? row.interactionType ?? row.interaction_type ?? 'Note',
      content: row.noteContent ?? row.content ?? row.body ?? row.note ?? '',
      author: row.createdByUser ?? row.createdBy ?? '',
      created_at:
        row.creationDate ?? row.created_at ?? row.date ?? row.createdAt ?? new Date().toISOString()
    }));
    setNotes((prev) => {
      const next = { ...prev, [member.id]: list };
      persist('mpr-notes', next);
      return next;
    });
    return list;
  }, []);

  /** Previous SMS communications for a member, loaded from the comms service. */
  const fetchSmsHistory = useCallback(async (member) => {
    const url = `${baseUrl}/api/client/GetCommHistoryByRefId?clientRefId=${encodeURIComponent(
      member.clientRefId || member.emp_no
    )}`;
    const response = await fetch(url);
    if (!response.ok)
      throw new Error(`The comms service responded with status ${response.status}.`);
    const payload = await response.json();
    const rows = Array.isArray(payload)
      ? payload
      : payload?.data ?? payload?.records ?? payload?.result ?? [];
    const list = (Array.isArray(rows) ? rows : []).map((row, index) => ({
      id: String(row.id ?? row.commId ?? index),
      body: row.body ?? row.message ?? row.content ?? '',
      phone: row.phone ?? row.msisdn ?? '',
      delivery_status: row.delivery_status ?? row.status ?? 'sent',
      created_at: row.created_at ?? row.date ?? row.createdAt ?? new Date().toISOString()
    }));
    setSms((prev) => {
      const next = { ...prev, [member.id]: list };
      persist('mpr-sms', next);
      return next;
    });
    return list;
  }, []);

  const savePhone = useCallback((member, phone) => {
    setPhones((prev) => {
      const next = { ...prev, [member.id]: phone.trim() };
      persist('mpr-phones', next);
      return next;
    });
  }, []);

  /** Browser-only SMS outbox: notices are recorded on this device until an SMS service is connected. */
  /** Send an SMS notice through the client service, one message per member. */
  const sendSms = useCallback(async (recipients, body) => {
    const reachable = recipients.filter((m) => (m.phone ?? '').trim().length >= 7);
    const at = new Date().toISOString();
    const results = [];

    await reachable.reduce(async (previousPromise, m) => {
      await previousPromise;
      const url = `${baseUrl}/api/Client/SendSmsBasic?clientRefId=${encodeURIComponent(
        m.clientRefId
      )}&cellNumber=${encodeURIComponent(m.phone.trim())}&message=${encodeURIComponent(body)}`;
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            clientRefId: m.clientRefId,
            cellNumber: m.phone.trim(),
            message: body
          })
        });
        results.push({ member: m, ok: response.ok, status: response.status });
      } catch {
        results.push({ member: m, ok: false, status: 0 });
      }
    }, Promise.resolve());

    const sent = results.filter((r) => r.ok).length;
    const failed = results.length - sent;

    setSms((prev) => {
      const next = results.reduce(
        (acc, { member, ok }) => ({
          ...acc,
          [member.id]: [
            {
              id: `${Date.now()}-${member.id}`,
              body,
              phone: member.phone,
              delivery_status: ok ? 'sent' : 'failed',
              created_at: at
            },
            ...(acc[member.id] ?? [])
          ]
        }),
        { ...prev }
      );

      persist('mpr-sms', next);
      return next;
    });

    return { sent, failed, skipped: recipients.length - reachable.length };
  }, []);

  const withPhones = useMemo(
    () => members.map((m) => (phones[m.id] ? { ...m, phone: phones[m.id] } : m)),
    [members, phones]
  );

  const value = {
    members: withPhones,
    loading,
    error,
    fetchMembers,
    provinces,
    fetchProvinces,
    notes,
    addNote,
    fetchNotes,
    sms,
    sendSms,
    fetchSmsHistory,
    savePhone
  };
  return <OnOffContext.Provider value={value}>{children}</OnOffContext.Provider>;
}

export function useMembersContext() {
  const ctx = useContext(OnOffContext);
  if (!ctx) throw new Error('useMembers must be used inside MembersProvider');
  return ctx;
}
